import type { UserStateV2Value } from '~/features/user/userState.v2.machine';

type UserStateValue = UserStateV2Value;

export interface RouteConfig {
  path: string;
  title: string;
  description?: string;
  icon?: string;
}

export type RoutePath =
  | '/landing'
  | '/logout'
  | '/home'
  | '/signIn'
  | '/sign-in'
  | '/sign-up'
  | '/provider-association/verify'
  | '/signUp/provider'
  | '/signUp/employer'
  | '/loginOrCreateAccount'
  | '/vault/dashboard'
  | '/vault/profile'
  | '/vault/aoi'
  | '/vault/settings'
  | '/vault/licenses'
  | '/vault/certifications'
  | '/vault/board-certifications'
  | '/vault/academics'
  | '/vault/documents'
  | '/vault/employment'
  | '/vault/privileges'
  | '/vault/attestations'
  | '/vault/peer-references'
  | '/vault/cme-training'
  | '/vault/malpractice'
  | '/vault/medical-license'
  | '/vault/faq'
  | '/hlx/dashboard'
  | '/hlx/settings'
  | '/hlx/calendar'
  | '/hlx/listings'
  | '/hlx/msaas-listings'
  | '/hlx/listing-search'
  | '/hlx/contracts'
  | '/hlx/billing'
  | '/hlx/billing/admin'
  | '/hlx/job-opportunities'
  | '/hlx/service-opportunities'
  | '/hlx/b2b-opportunities'
  | '/hlx/my-services'
  | '/hlx/provider-associations'
  | '/hlx/provider-view'
  | '/hlx/search'
  | '/hlx/employer-info'
  | '/hlx/facilities'
  | '/hlx/team'
  | '/hlx/impersonation'
  | '/hlx/faq'
  | '/employer-user-invitation/verify'
  | '/employer-user-invitation/accept'
  | '/register/role-select'
  | '/register/provider'
  | '/register/employer';

export const PUBLIC_ROUTES: RouteConfig[] = [
  { path: '/landing', title: 'Landing' },
  { path: '/signIn', title: 'Sign In' },
  { path: '/sign-in', title: 'Sign In' },
  { path: '/signUp/provider', title: 'Provider Sign Up' },
  { path: '/signUp/employer', title: 'Employer Sign Up' },
  { path: '/loginOrCreateAccount', title: 'Login Or Create Account' },
  { path: '/provider-association/verify', title: 'Provider Association Invitation' },
  { path: '/employer-user-invitation/verify', title: 'Employer User Invitation' },
];

export const PROVIDER_PAGE_KEYS = {
  dashboard: 'dashboard',
  personalProfile: 'profile',
  aoi: 'aoi',
  settings: 'settings',
  academics: 'academics',
  employment: 'employment',
  medicalLicense: 'medical-license',
  licenses: 'licenses',
  boardCertifications: 'board-certifications',
  certifications: 'certifications',
  cmeTraining: 'cme-training',
  privileges: 'privileges',
  malpractice: 'malpractice',
  attestations: 'attestations',
  peerReferences: 'peer-references',
  documents: 'documents',
  // [MCV-1724] The in-app FAQ the header ? opens. Not in VAULT_ROUTES: that list feeds the
  // dashboard feature cards and side-nav metadata, and the FAQ is neither.
  faq: 'faq',
};

/** [MCV-1753] Page keys vaultState's ON_SET_PAGE has a branch for. The FAQ has none. */
export const VAULT_MACHINE_PAGE_KEYS: ReadonlySet<string> = new Set(
  Object.values(PROVIDER_PAGE_KEYS).filter((k) => k !== PROVIDER_PAGE_KEYS.faq)
);
export const vaultPageFromPathname = (pathname: string): string | undefined => {
  const [, root, page] = pathname.split('/');
  return root === 'vault' && page && VAULT_MACHINE_PAGE_KEYS.has(page) ? page : undefined;
};

export const MARKET_PAGE_KEYS = {
  dashboard: 'dashboard',
  settings: 'settings',
  calendar: 'calendar',
  listings: 'listings',
  msaasListings: 'msaas-listings',
  listingSearch: 'listing-search',
  contracts: 'contracts',
  billing: 'billing',
  billingAdmin: 'billing/admin',
  jobOpportunities: 'job-opportunities',
  serviceOpportunities: 'service-opportunities',
  b2bOpportunities: 'b2b-opportunities',
  myServices: 'my-services',
  providerAssociations: 'provider-associations',
  providerView: 'provider-view',
  search: 'search',
  employerInfo: 'employer-info',
  facilities: 'facilities',
  team: 'team',
  impersonation: 'impersonation',
  // [MCV-1724] The in-app FAQ the header ? opens. Not in HLX_ROUTES, for the same reason.
  faq: 'faq',
};

export const VAULT_ROUTES: RouteConfig[] = [
  {
    path: `/vault/${PROVIDER_PAGE_KEYS.dashboard}`,
    title: 'Dashboard',
    description: 'Overview of your MedCredVault profile',
    icon: '🏠',
  },
  {
    path: `/vault/${PROVIDER_PAGE_KEYS.personalProfile}`,
    title: 'Profile',
    description: 'Manage your personal information',
    icon: '👤',
  },
  {
    path: `/vault/${PROVIDER_PAGE_KEYS.aoi}`,
    title: 'Areas of Interest',
    description: 'Manage your areas of interest',
    icon: '🏷️',
  },
  {
    path: `/vault/${PROVIDER_PAGE_KEYS.settings}`,
    title: 'Settings',
    description: 'Account settings',
    icon: '⚙️',
  },
  {
    path: `/vault/${PROVIDER_PAGE_KEYS.academics}`,
    title: 'Academics',
    description: 'Academic background and education',
    icon: '🎓',
  },
  {
    path: `/vault/${PROVIDER_PAGE_KEYS.employment}`,
    title: 'Employment',
    description: 'Employment history and positions',
    icon: '💼',
  },
  {
    path: `/vault/${PROVIDER_PAGE_KEYS.medicalLicense}`,
    title: 'Medical Licenses',
    description: 'Medical license information',
    icon: '💉',
  },
  {
    path: `/vault/${PROVIDER_PAGE_KEYS.licenses}`,
    title: 'Licenses',
    description: 'View and manage professional licenses',
    icon: '📜',
  },
  {
    path: `/vault/${PROVIDER_PAGE_KEYS.boardCertifications}`,
    title: 'Board Certifications',
    description: 'Board certifications',
    icon: '🎖️',
  },
  {
    path: `/vault/${PROVIDER_PAGE_KEYS.certifications}`,
    title: 'Certifications',
    description: 'Other certifications and credentials',
    icon: '📋',
  },
  {
    path: `/vault/${PROVIDER_PAGE_KEYS.cmeTraining}`,
    title: 'CME Training',
    description: 'CME training and credits',
    icon: '📚',
  },
  {
    path: `/vault/${PROVIDER_PAGE_KEYS.privileges}`,
    title: 'Privileges',
    description: 'Hospital privileges and affiliations',
    icon: '🏥',
  },
  {
    path: `/vault/${PROVIDER_PAGE_KEYS.malpractice}`,
    title: 'Malpractice',
    description: 'Malpractice claims and insurance',
    icon: '⚖️',
  },
  {
    path: `/vault/${PROVIDER_PAGE_KEYS.attestations}`,
    title: 'Attestations',
    description: 'Attestations and verifications',
    icon: '✍️',
  },
  {
    path: `/vault/${PROVIDER_PAGE_KEYS.peerReferences}`,
    title: 'Peer References',
    description: 'Peer references',
    icon: '👥',
  },
  {
    path: `/vault/${PROVIDER_PAGE_KEYS.documents}`,
    title: 'Documents',
    description: 'Upload and manage documents',
    icon: '📄',
  },
];

export const HLX_ROUTES: RouteConfig[] = [
  {
    path: `/hlx/${MARKET_PAGE_KEYS.dashboard}`,
    title: 'Dashboard',
    description: 'Overview of HealthcareLX portal',
    icon: '🏠',
  },
  {
    path: `/hlx/${MARKET_PAGE_KEYS.calendar}`,
    title: 'Calendar',
    description: 'Manage availability and upcoming marketplace dates',
    icon: '📅',
  },
  {
    path: `/hlx/${MARKET_PAGE_KEYS.listings}`,
    title: 'Job Listings',
    description: 'Manage job listings',
    icon: '📋',
  },
  {
    path: `/hlx/${MARKET_PAGE_KEYS.msaasListings}`,
    title: 'B2B Listings',
    description: 'Manage B2B listings',
    icon: '📋',
  },
  {
    path: `/hlx/${MARKET_PAGE_KEYS.listingSearch}`,
    title: 'B2B Marketplace',
    description: 'Search for B2B job opportunities',
    icon: '🔍',
  },
  {
    path: `/hlx/${MARKET_PAGE_KEYS.contracts}`,
    title: 'Contracts',
    description: 'Review, sign, and manage your contracts',
    icon: '📝',
  },
  {
    path: `/hlx/${MARKET_PAGE_KEYS.billing}`,
    title: 'Billing',
    description: 'Payment methods, credits, and invoices',
    icon: '💳',
  },
  {
    path: `/hlx/${MARKET_PAGE_KEYS.billingAdmin}`,
    title: 'Billing Credits',
    description: 'Administer promotional credits',
    icon: '💳',
  },
  {
    path: `/hlx/${MARKET_PAGE_KEYS.jobOpportunities}`,
    title: 'Job Opportunities',
    description: 'Job opportunity responses',
    icon: '💼',
  },
  {
    path: `/hlx/${MARKET_PAGE_KEYS.b2bOpportunities}`,
    title: 'B2B Responses',
    description: 'Review B2B opportunity responses',
    icon: '💼',
  },
  {
    path: `/hlx/${MARKET_PAGE_KEYS.serviceOpportunities}`,
    title: 'Service Responses',
    description: 'Read and reply to service inquiries',
    icon: '💬',
  },
  {
    path: `/hlx/${MARKET_PAGE_KEYS.myServices}`,
    title: 'My Services',
    description: 'Manage provider service listings',
    icon: '📋',
  },
  {
    path: `/hlx/${MARKET_PAGE_KEYS.providerAssociations}`,
    title: 'Provider Associations',
    description: 'Manage provider associations',
    icon: '🤝',
  },
  {
    path: `/hlx/${MARKET_PAGE_KEYS.search}`,
    title: 'Search',
    description: 'Search for providers or jobs',
    icon: '🔍',
  },
  {
    path: `/hlx/${MARKET_PAGE_KEYS.employerInfo}`,
    title: 'Employer Info',
    description: 'Manage organization information',
    icon: '🏢',
  },
  {
    path: `/hlx/${MARKET_PAGE_KEYS.facilities}`,
    title: 'Facilities',
    description: 'Manage facility information',
    icon: '🏢',
  },
  {
    path: `/hlx/${MARKET_PAGE_KEYS.team}`,
    title: 'Team',
    description: 'Manage colleague invitations',
    icon: '👥',
  },
  {
    path: `/hlx/${MARKET_PAGE_KEYS.impersonation}`,
    title: 'Impersonation',
    description: 'Employer: Impersonate provider accounts',
    icon: '🔄',
  },
];

export const REGISTRATION_ROUTES: RouteConfig[] = [
  { path: '/register/role-select', title: 'Select Role' },
  { path: '/register/provider', title: 'Provider Registration' },
  { path: '/register/employer', title: 'Employer Registration' },
];

export const VAULT_FEATURE_CARDS = VAULT_ROUTES.slice(1);
export const HLX_FEATURE_CARDS = HLX_ROUTES.slice(1);

export function getRouteByPath(path: string): RouteConfig | undefined {
  const allRoutes = [...PUBLIC_ROUTES, ...VAULT_ROUTES, ...HLX_ROUTES, ...REGISTRATION_ROUTES];
  return allRoutes.find((route) => route.path === path);
}

export function getRoutesByBrand(brand: 'vault' | 'hlx'): RouteConfig[] {
  return brand === 'vault' ? VAULT_ROUTES : HLX_ROUTES;
}

export const paths = {
  landing: '/',
  logout: '/logout',
  // Role-aware "Go to dashboard" target for the public header. [MCV-1723] Not a
  // dashboard itself and deliberately absent from every route registry: it
  // resolves to the signed-in user's own home through RoleHomeRedirect.
  appHome: '/home',
  publicAuth: {
    signIn: '/sign-in',
    signUp: '/sign-up',
    signInLegacy: '/signIn',
    signUpProvider: '/signUp/provider',
    signUpEmployer: '/signUp/employer',
    loginOrCreateAccount: '/loginOrCreateAccount',
    forgotPassword: '/forgot-password',
    ssoCallback: '/sso-callback',
    authComplete: '/auth/complete',
  },
  sessionTasks: {
    chooseOrganization: '/session-tasks/choose-organization',
    resetPassword: '/session-tasks/reset-password',
    setupMfa: '/session-tasks/setup-mfa',
  },
  providerAssociation: {
    verify: '/provider-association/verify',
  },
  legacy: {
    providerRegistrationBase: '/provider-registration',
  },
  vault: {
    dashboard: `/vault/${PROVIDER_PAGE_KEYS.dashboard}`,
    profile: `/vault/${PROVIDER_PAGE_KEYS.personalProfile}`,
    aoi: `/vault/${PROVIDER_PAGE_KEYS.aoi}`,
    settings: `/vault/${PROVIDER_PAGE_KEYS.settings}`,
    academics: `/vault/${PROVIDER_PAGE_KEYS.academics}`,
    employment: `/vault/${PROVIDER_PAGE_KEYS.employment}`,
    medicalLicense: `/vault/${PROVIDER_PAGE_KEYS.medicalLicense}`,
    licenses: `/vault/${PROVIDER_PAGE_KEYS.licenses}`,
    boardCertifications: `/vault/${PROVIDER_PAGE_KEYS.boardCertifications}`,
    certifications: `/vault/${PROVIDER_PAGE_KEYS.certifications}`,
    cmeTraining: `/vault/${PROVIDER_PAGE_KEYS.cmeTraining}`,
    privileges: `/vault/${PROVIDER_PAGE_KEYS.privileges}`,
    malpractice: `/vault/${PROVIDER_PAGE_KEYS.malpractice}`,
    attestations: `/vault/${PROVIDER_PAGE_KEYS.attestations}`,
    peerReferences: `/vault/${PROVIDER_PAGE_KEYS.peerReferences}`,
    documents: `/vault/${PROVIDER_PAGE_KEYS.documents}`,
    faq: `/vault/${PROVIDER_PAGE_KEYS.faq}`,
  },
  hlx: {
    dashboard: `/hlx/${MARKET_PAGE_KEYS.dashboard}`,
    settings: `/hlx/${MARKET_PAGE_KEYS.settings}`,
    calendar: `/hlx/${MARKET_PAGE_KEYS.calendar}`,
    listings: `/hlx/${MARKET_PAGE_KEYS.listings}`,
    msaasListings: `/hlx/${MARKET_PAGE_KEYS.msaasListings}`,
    listingSearch: `/hlx/${MARKET_PAGE_KEYS.listingSearch}`,
    contracts: `/hlx/${MARKET_PAGE_KEYS.contracts}`,
    billing: `/hlx/${MARKET_PAGE_KEYS.billing}`,
    billingAdmin: `/hlx/${MARKET_PAGE_KEYS.billingAdmin}`,
    jobOpportunities: `/hlx/${MARKET_PAGE_KEYS.jobOpportunities}`,
    serviceOpportunities: `/hlx/${MARKET_PAGE_KEYS.serviceOpportunities}`,
    b2bOpportunities: `/hlx/${MARKET_PAGE_KEYS.b2bOpportunities}`,
    myServices: `/hlx/${MARKET_PAGE_KEYS.myServices}`,
    providerAssociations: `/hlx/${MARKET_PAGE_KEYS.providerAssociations}`,
    providerView: `/hlx/${MARKET_PAGE_KEYS.providerView}`,
    search: `/hlx/${MARKET_PAGE_KEYS.search}`,
    employerInfo: `/hlx/${MARKET_PAGE_KEYS.employerInfo}`,
    facilities: `/hlx/${MARKET_PAGE_KEYS.facilities}`,
    team: `/hlx/${MARKET_PAGE_KEYS.team}`,
    impersonation: `/hlx/${MARKET_PAGE_KEYS.impersonation}`,
    faq: `/hlx/${MARKET_PAGE_KEYS.faq}`,
  },
  employerUserInvitation: {
    verify: '/employer-user-invitation/verify',
    accept: '/employer-user-invitation/accept',
  },
  register: {
    roleSelect: '/register/role-select',
    provider: '/register/provider',
    employer: '/register/employer',
  },
} as const;

// Route patterns the post-auth resolvers accept as a preserved returnTo
// (see postAuthRedirect.ts). `paths.hlx` / `paths.vault` are exactly what
// AuthenticatedRoutes mounts, so deriving the lists from them keeps the guard
// in lockstep with the route table. HLX_ROUTES / VAULT_ROUTES are side-nav
// card config, not a route registry — HLX_ROUTES omits provider-view.
export const HLX_ROUTE_PATTERNS: readonly string[] = Object.values(paths.hlx);
export const VAULT_ROUTE_PATTERNS: readonly string[] = Object.values(paths.vault);
export const REGISTRATION_ROUTE_PATTERNS: readonly string[] = Object.values(paths.register);

export type AllowedStates = UserStateValue | UserStateValue[];
